// \src\components\feature\Navbar.tsx

import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAdmin } from "@/context/AdminContext";

const navLinks = [
  { label: "Início", path: "/" },
  { label: "Promoções", path: "/promocoes" },
  { label: "MZ Outlet", path: "/outlet" },
  { label: "Sorteio", path: "/sorteio" },
  { label: "Vendedores", path: "/vendedores" },
  { label: "Produtos", path: "/produtos" },
  { label: "A Empresa", path: "/empresa" },
  { label: "Notícias", path: "/noticias" },
  { label: "Contato", path: "/contato" },
];

export default function Navbar({ embedded = false }: { embedded?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { data } = useAdmin();

  useEffect(() => {
    if (embedded) return;
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [embedded]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  const headerClass = embedded
    ? "relative w-full bg-background-50 shadow-sm"
    : [
        "fixed top-0 left-0 right-0 z-[100] transition-all duration-300",
        scrolled || menuOpen
          ? "bg-background-50 shadow-sm"
          : "bg-white/95 backdrop-blur-sm shadow-sm",
      ].join(" ");

  return (
    <header className={headerClass}>
      <nav className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer">
            <img
              src={data.settings.logoUrl}
              alt={data.settings.siteName}
              className="h-16 md:h-20 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 text-sm font-medium font-label uppercase tracking-wide transition-all duration-200 whitespace-nowrap cursor-pointer relative group ${
                  isActive(link.path)
                    ? "text-primary-500"
                    : scrolled
                    ? "text-foreground-800 hover:text-primary-500"
                    : "text-foreground-950 hover:text-primary-500"
                }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 h-0.5 bg-primary-500 transition-all duration-200 ${
                    isActive(link.path) ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={data.settings.socialWhatsapp}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 rounded-md text-sm font-label font-medium transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-base" />
              WhatsApp
            </a>
          </div>

          {/* Mobile Actions */}
          <div className="lg:hidden flex items-center gap-2 shrink-0">
            <a
              href={data.settings.socialWhatsapp}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-11 h-11 flex items-center justify-center text-[#CC0000] hover:text-primary-600 transition-colors duration-200 cursor-pointer"
              aria-label="Falar com a M&Z pelo WhatsApp"
              title="WhatsApp M&Z"
            >
              <i className="ri-whatsapp-line text-[34px] leading-none" aria-hidden="true" />
            </a>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`lg:hidden p-2 rounded-md cursor-pointer transition-colors duration-200 ${
                scrolled || menuOpen ? "text-foreground-800" : "text-foreground-950"
              }`}
              aria-label="Abrir menu"
            >
              <i className={`text-2xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="lg:hidden bg-background-50 border-t border-background-200 pb-4 animate-fade-in">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 text-sm font-label font-medium uppercase tracking-wide transition-colors cursor-pointer ${
                  isActive(link.path)
                    ? "text-primary-500 bg-primary-50"
                    : "text-foreground-800 hover:text-primary-500 hover:bg-background-100"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="px-4 pt-2">
              <a
                href={data.settings.socialWhatsapp}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-3 rounded-md text-sm font-label font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base" />
                Fale pelo WhatsApp
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}